import React, { Component, ErrorInfo, ReactNode } from 'react';

/**
 * OSM-1983 — App-level React error boundary.
 *
 * NOTE ON CONVENTION: The codebase-wide React guideline is "functional
 * components only". This component is the one legitimate exception —
 * React only supports error boundaries via class components (the
 * `componentDidCatch` and `getDerivedStateFromError` lifecycle hooks
 * have no functional-component equivalent). Do NOT convert this to a
 * functional component.
 *
 * Renders a graceful fallback UI when any descendant throws during
 * render, in a lifecycle method, or in the constructor of the whole
 * tree it wraps. Kept intentionally scoped to the `<Routes>` block in
 * `App.tsx` so that a crash inside a page does NOT unmount the
 * `<Sidebar>` and lock the user out of switching account.
 */
interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  handleGoHome = (): void => {
    window.location.href = '/home';
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 48 48"
            aria-hidden="true"
            className="w-16 h-16 mb-6"
          >
            <path
              d="M24 4 L44 40 H4 Z"
              fill="#FEF3C7"
              stroke="#F59E0B"
              strokeWidth="3"
              strokeLinejoin="round"
            />
            <rect x="22" y="18" width="4" height="12" rx="1" fill="#B45309" />
            <circle cx="24" cy="34" r="2" fill="#B45309" />
          </svg>
          <h2 className="text-xl font-bold text-primarygray mb-2">
            Something went wrong
          </h2>
          <p className="text-base text-secondarygray max-w-md mb-6">
            We hit an unexpected error while rendering this page. Try selecting
            a different account from the sidebar, or reload the page.
          </p>
          <div className="flex gap-3">
            <button
              onClick={this.handleGoHome}
              className="text-sm font-bold py-2 px-6 rounded-full bg-success text-light hover:opacity-90 transition-opacity"
            >
              GO TO HOME
            </button>
            <button
              onClick={this.handleReload}
              className="text-sm font-bold py-2 px-6 rounded-full border border-thirdgray text-thirdgray hover:bg-gray50 transition-colors"
            >
              RELOAD PAGE
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
